/**
 * W-6 · Incidents.
 *
 * Le filtre `status` est envoyé au serveur (`GET /incidents?status=`), les
 * filtres catégorie et ligne sont appliqués côté client : le contrat ne les
 * expose pas en paramètres, et la file d'un événement de deux semaines reste
 * petite. Le jour où elle grossit, la bascule se fait en changeant `query` dans
 * `api.incidents` — les listes de valeurs, elles, sont déjà déduites des données.
 */

import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Incident } from '../lib/api';
import { rangeKey } from '../lib/range';
import type { Range } from '../lib/range';
import { useApi } from '../lib/useApi';
import { toast } from '../components/Toast';
import { formatDateTime, formatInt } from '../lib/format';
import { Async, Badge, Banner, Card, Kpi } from '../components/ui';
import type { Tone } from '../components/ui';
import { lineIdentity } from '../lib/lines';
import { READ_ONLY_NOTICE, useCan } from '../lib/session';

const STATUS_LABELS: Record<string, string> = {
  open: 'Ouvert',
  ack: 'Pris en charge',
  resolved: 'Résolu',
};

const SEVERITY_LABELS: Record<string, string> = {
  low: 'Faible',
  medium: 'Moyenne',
  high: 'Élevée',
};

const CATEGORY_LABELS: Record<string, string> = {
  delay: 'Retard',
  crowding: 'Affluence',
  breakdown: 'Panne',
  route: 'Itinéraire',
  safety: 'Sécurité',
  app: 'Application',
  other: 'Autre',
};

function statusTone(status: string): Tone {
  return status === 'resolved' ? 'ok' : status === 'ack' ? 'info' : 'warn';
}
function severityTone(severity: string): Tone {
  return severity === 'high' ? 'bad' : severity === 'medium' ? 'warn' : 'neutral';
}

/** Progression naturelle de la file : ouvert → pris en charge → résolu. */
const NEXT_STATUS: Record<string, string | undefined> = { open: 'ack', ack: 'resolved' };

export function IncidentsPage({ range }: { range: Range }): React.ReactElement {
  // Faire avancer un incident est une écriture : le serveur la refuse en 403
  // pour un `viewer`. On aligne l'écran plutôt que de lui offrir un bouton qui
  // échouera à coup sûr.
  const canWrite = useCan('incidents:write');
  const [status, setStatus] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [line, setLine] = useState<string>('');
  const [pending, setPending] = useState<string | null>(null);
  const [patchError, setPatchError] = useState<ApiError | null>(null);
  const [patched, setPatched] = useState<Record<string, Incident>>({});

  const list = useApi(
    (s) => api.incidents({ status: status || undefined, from: range.from, to: range.to }, s),
    `${status}|${rangeKey(range)}`,
  );

  // Les incidents modifiés localement écrasent la liste en attendant le
  // prochain chargement : sans ça, le tableau reviendrait à l'ancien statut
  // jusqu'au rechargement et l'opérateur croirait son clic perdu.
  const rows = useMemo(
    () => (list.data ?? []).map((inc) => patched[inc.id] ?? inc),
    [list.data, patched],
  );

  const categories = useMemo(() => [...new Set(rows.map((r) => r.category))].sort(), [rows]);
  const lineNos = useMemo(
    () => [...new Set(rows.map((r) => r.lineNo).filter((v): v is string => v !== null))].sort(),
    [rows],
  );

  const filtered = rows.filter(
    (r) => (category === '' || r.category === category) && (line === '' || r.lineNo === line),
  );

  const counts = {
    open: rows.filter((r) => r.status === 'open').length,
    ack: rows.filter((r) => r.status === 'ack').length,
    resolved: rows.filter((r) => r.status === 'resolved').length,
  };

  const applyStatus = async (incident: Incident, next: string): Promise<void> => {
    setPending(incident.id);
    setPatchError(null);
    try {
      const updated = await api.patchIncident(incident.id, { status: next });
      setPatched((prev) => ({ ...prev, [incident.id]: updated }));
      toast(`Signalement : ${STATUS_LABELS[next] ?? next}`);
    } catch (cause) {
      setPatchError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="stack">
      <div className="grid grid-kpi">
        {/* La file à traiter est le chiffre autour duquel la garde s'organise :
            c'est lui qui porte la tuile pleine. */}
        <Kpi
          featured
          label="Ouverts"
          value={formatInt(counts.open)}
          hint="Signalements non encore pris en charge."
        />
        <Kpi label="Pris en charge" value={formatInt(counts.ack)} hint="Un opérateur du TROC les traite." />
        <Kpi label="Résolus" value={formatInt(counts.resolved)} hint="Clos sur la période sélectionnée." />
      </div>

      {canWrite ? null : (
        <Banner tone="info" title="Lecture seule">
          {READ_ONLY_NOTICE} La file reste consultable et filtrable ; demandez à un opérateur TROC ou à un
          administrateur de faire avancer un signalement.
        </Banner>
      )}

      {patchError ? (
        <Banner tone="bad" title="Mise à jour refusée">
          {patchError.userMessage}
          {patchError.problem.detail ? ` : ${patchError.problem.detail}` : ''}
        </Banner>
      ) : null}

      <Card
        title={`File des incidents · ${range.label}`}
        hint="La note est écrite par l'opérateur. Rien de ce que l'athlète tape sur son téléphone n'est conservé."
        action={
          <button type="button" className="btn btn-sm" onClick={list.reload}>
            Recharger
          </button>
        }
      >
        <div className="toolbar" style={{ marginBottom: 14 }}>
          <label className="field">
            Statut
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Tous</option>
              <option value="open">Ouvert</option>
              <option value="ack">Pris en charge</option>
              <option value="resolved">Résolu</option>
            </select>
          </label>
          <label className="field">
            Catégorie
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Toutes</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c] ?? c}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Ligne
            <select value={line} onChange={(e) => setLine(e.target.value)}>
              <option value="">Toutes</option>
              {lineNos.map((l) => (
                <option key={l} value={l}>
                  {lineIdentity(l).name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Async state={list}>
          {() =>
            filtered.length === 0 ? (
              <div className="state state-empty">
                <span className="state-title">Aucun incident</span>
                <span>
                  {rows.length === 0
                    ? 'Aucun signalement sur la période sélectionnée.'
                    : 'Aucun incident ne correspond aux filtres choisis.'}
                </span>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Ouvert le</th>
                      <th>Catégorie</th>
                      <th>Ligne</th>
                      <th>Gravité</th>
                      <th>Source</th>
                      <th>Statut</th>
                      <th>Note</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((inc) => {
                      const next = NEXT_STATUS[inc.status];
                      const identity = inc.lineNo === null ? null : lineIdentity(inc.lineNo);
                      return (
                        <tr key={inc.id}>
                          <td className="mono">{formatDateTime(inc.createdAt)}</td>
                          <td>{CATEGORY_LABELS[inc.category] ?? inc.category}</td>
                          <td>
                            {identity === null ? (
                              <span className="muted">sans ligne</span>
                            ) : (
                              <span className="row">
                                <i
                                  className="dot-line"
                                  style={{ background: identity.color }}
                                  aria-hidden="true"
                                />
                                {identity.name}
                              </span>
                            )}
                          </td>
                          <td>
                            <Badge tone={severityTone(inc.severity)}>
                              {SEVERITY_LABELS[inc.severity] ?? inc.severity}
                            </Badge>
                          </td>
                          <td className="muted">{inc.source === 'troc' ? 'TROC' : 'App'}</td>
                          <td>
                            <Badge tone={statusTone(inc.status)}>
                              {STATUS_LABELS[inc.status] ?? inc.status}
                            </Badge>
                          </td>
                          <td className="small muted">{inc.note ?? <span className="muted">sans note</span>}</td>
                          <td>
                            {next === undefined ? (
                              <span className="muted small">clos</span>
                            ) : (
                              <button aria-busy={pending === inc.id}
                                type="button"
                                className="btn btn-sm"
                                disabled={pending === inc.id || !canWrite}
                                title={canWrite ? undefined : READ_ONLY_NOTICE}
                                onClick={() => void applyStatus(inc, next)}
                              >
                                {`→ ${STATUS_LABELS[next] ?? next}`}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </Card>
    </div>
  );
}
